<script setup lang="ts">
import type Tech from '@/types/Tech.ts'
import { computed } from 'vue'
import { useGameStore } from '@/stores/gameStore.ts'
import { useAssetStore } from '@/stores/assetStore.ts'
import { CDN } from '@/models/cdn.ts'
import StandardCard from '@/components/Elements/Cards/StandardCard.vue'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import GenericIconWithNumber from '@/components/Elements/Images/Icons/GenericIconWithNumber.vue'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import Backend from '@/models/backend.ts'
import DeleteButton from '@/components/Elements/Buttons/DeleteButton.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

/**
 * Stores / models
 */
const gameStore = useGameStore()
const assetStore = useAssetStore()

/**
 * Props
 */
const props = defineProps<{
  tech: Tech
}>()

/**
 * Functions
 */

// Set as target tech and update queue
const setTargetTech = () => {
  const backend = new Backend()
  // Get current tech and queue
  const targetTechId = gameStore.player?.targetTechId
  const currentQueue = [...(gameStore.playerTechQueue.queue || [])]

  // First save the current tech to the queue if it exists and isn't already in queue
  if (
    targetTechId &&
    targetTechId !== 0 &&
    targetTechId !== props.tech.id &&
    !currentQueue.includes(targetTechId)
  ) {
    // Add current tech to beginning of queue
    currentQueue.unshift(targetTechId)
  }

  // Remove the tech we're setting as target from the queue if it's there
  const index = currentQueue.indexOf(props.tech.id)
  if (index >= 0) {
    currentQueue.splice(index, 1)
  }

  // Update queue first to ensure current tech is saved
  backend.updateTechQueue(currentQueue).then(() => {
    // Then set the new target tech
    backend.setTechTarget(props.tech.id).then(() => {
      gameStore.setTinySuccessToastMessage(t('modals.techQueueItem.messages.techSetAsTarget'))
    })
  })
}

const moveUp = () => {
  const backend = new Backend()
  const currentQueue = [...(gameStore.playerTechQueue.queue || [])]
  const index = currentQueue.indexOf(props.tech.id)
  if (index > 0) {
    // Swap positions
    ;[currentQueue[index], currentQueue[index - 1]] = [currentQueue[index - 1], currentQueue[index]]
    backend.updateTechQueue(currentQueue).then(() => {
      gameStore.setTinySuccessToastMessage(t('modals.techQueueItem.messages.movedUp'))
    })
  }
}

const moveDown = () => {
  const backend = new Backend()
  const currentQueue = [...(gameStore.playerTechQueue.queue || [])]
  const index = currentQueue.indexOf(props.tech.id)
  if (index >= 0 && index < currentQueue.length - 1) {
    // Swap positions
    ;[currentQueue[index], currentQueue[index + 1]] = [currentQueue[index + 1], currentQueue[index]]
    backend.updateTechQueue(currentQueue).then(() => {
      gameStore.setTinySuccessToastMessage(t('modals.techQueueItem.messages.movedDown'))
    })
  }
}

const moveToTop = () => {
  const backend = new Backend()
  const currentQueue = [...(gameStore.playerTechQueue.queue || [])]
  const index = currentQueue.indexOf(props.tech.id)
  if (index > 0) {
    // Remove from current position and add to beginning
    currentQueue.splice(index, 1)
    currentQueue.unshift(props.tech.id)
    backend.updateTechQueue(currentQueue).then(() => {
      gameStore.setTinySuccessToastMessage(t('modals.techQueueItem.messages.movedToTop'))
    })
  }
}

const moveToBottom = () => {
  const backend = new Backend()
  const currentQueue = [...(gameStore.playerTechQueue.queue || [])]
  const index = currentQueue.indexOf(props.tech.id)
  if (index >= 0 && index < currentQueue.length - 1) {
    // Remove from current position and add to end
    currentQueue.splice(index, 1)
    currentQueue.push(props.tech.id)
    backend.updateTechQueue(currentQueue).then(() => {
      gameStore.setTinySuccessToastMessage(t('modals.techQueueItem.messages.movedToBottom'))
    })
  }
}

const removeFromQueue = () => {
  const backend = new Backend()
  const currentQueue = [...(gameStore.playerTechQueue.queue || [])]
  const index = currentQueue.indexOf(props.tech.id)
  if (index >= 0) {
    currentQueue.splice(index, 1)
    backend.updateTechQueue(currentQueue).then(() => {
      gameStore.setTinySuccessToastMessage(t('modals.techQueueItem.messages.removed'))
    })
  }
}

/**
 * Computed
 */

// Compute position in queue
const queuePosition = computed(() => {
  if (!gameStore.playerTechQueue.queue) return -1
  return gameStore.playerTechQueue.queue.indexOf(props.tech.id)
})

// Check if tech can be moved up in queue
const canMoveUp = computed(() => {
  return queuePosition.value > 0
})

// Check if tech can be moved down in queue
const canMoveDown = computed(() => {
  if (!gameStore.playerTechQueue.queue) return false
  return (
    queuePosition.value >= 0 && queuePosition.value < gameStore.playerTechQueue.queue.length - 1
  )
})

const provideIcons = computed<string[]>(() => {
  const icons: string[] = []

  for (const benefit of props.tech.provides) {
    if (benefit.type === 'buildings') {
      const effects = assetStore.getBuildingEffectsByCode(benefit.code) as any
      for (const effect of effects) {
        if (effect.bonus > 0) {
          const type = CDN.normalize(effect.type)
          // Prevent duplicate icons
          if (!icons.includes(type)) {
            icons.push(type)
          }
        }
      }
    } else if (benefit.type === 'ship_components') {
      const effects = assetStore.getShipComponentEffectsByCode(benefit.code) as any
      for (const effect of effects) {
        if (effect.modifier > 0) {
          if (!['ship_mass', 'ship_production_cost', 'ore'].includes(effect.type)) {
            const type = CDN.normalize(effect.type)
            if (!icons.includes(type)) {
              icons.push(type)
            }
          }
        }
      }
    }
  }

  return icons
})
</script>

<template>
  <StandardCard
    :title="t(`research.techs.${props.tech.code}.name`)"
    :class="{
      'w-full ': true
    }"
    top-padding="0"
    bottom-padding="2"
  >
    <div class="flex">
      <img
        :src="CDN.techIcon(props.tech.code)"
        alt=""
        class="rounded shrink-0"
        style="width: 5rem !important; height: 5rem !important"
      />
      <div class="ps-2 flex flex-col justify-between w-full">
        <div class="flex justify-between">
          <div class="flex-shrink-0">
            <GenericIconWithNumber :amount="props.tech.cost" type="science" />
          </div>
          <div class="flex-shrink-0">
            <template v-for="icon in provideIcons" :key="icon">
              <GenericIcon :type="icon" class="ml-2" />
            </template>
          </div>
        </div>
        <div class="flex justify-end">
          <div v-if="gameStore.playerTechQueue.queue?.includes(props.tech.id)" class="flex gap-1">
            <ActionButton
              @click.stop="setTargetTech"
              :for-icon="true"
              :title="t('modals.techQueueItem.setAsTarget')"
              class=""
            >
              <GenericIcon :tooltip="t('modals.techQueueItem.setAsTarget')" type="science" />
            </ActionButton>
            <ActionButton
              @click.stop="moveToTop"
              :disabled="!canMoveUp"
              :class="{ 'opacity-50 cursor-not-allowed': !canMoveUp }"
              :for-icon="true"
              :title="t('modals.techQueueItem.moveToTop')"
            >
              <GenericIcon :tooltip="t('modals.techQueueItem.moveToTop')" type="top" />
            </ActionButton>
            <ActionButton
              @click.stop="moveUp"
              :disabled="!canMoveUp"
              :class="{ 'opacity-50 cursor-not-allowed': !canMoveUp }"
              :for-icon="true"
              :title="t('modals.techQueueItem.moveUp')"
            >
              <GenericIcon :tooltip="t('modals.techQueueItem.moveUp')" type="up" />
            </ActionButton>
            <ActionButton
              @click.stop="moveDown"
              :disabled="!canMoveDown"
              :class="{ 'opacity-50 cursor-not-allowed': !canMoveDown }"
              :for-icon="true"
              :title="t('modals.techQueueItem.moveDown')"
            >
              <GenericIcon :tooltip="t('modals.techQueueItem.moveDown')" type="down" />
            </ActionButton>
            <ActionButton
              @click.stop="moveToBottom"
              :disabled="!canMoveDown"
              :class="{ 'opacity-50 cursor-not-allowed': !canMoveDown }"
              :for-icon="true"
              :title="t('modals.techQueueItem.moveToBottom')"
            >
              <GenericIcon :tooltip="t('modals.techQueueItem.moveToBottom')" type="bottom" />
            </ActionButton>
            <DeleteButton @click.stop="removeFromQueue" :for-icon="true" :title="t('modals.techQueueItem.removeFromQueue')">
              <GenericIcon :tooltip="t('modals.techQueueItem.removeFromQueue')" type="trash" />
            </DeleteButton>
          </div>
          <div v-else class="text-green-400">{{ t('modals.techQueueItem.currentTarget') }}</div>
        </div>
      </div>
    </div>
  </StandardCard>
</template>

<style scoped lang="scss"></style>
